import { errorMessageFrom } from "../../../../../../utils/formErrors";
import React, { useState, useEffect } from 'react';
import { Box, Stack, CircularProgress } from '@mui/material';
import TopBar from '../../../../../../component/TopBar';
import BtnPrimary from '../../../../../../component/PrimaryButton';
import BtnSecondary from '../../../../../../component/SecondaryButton';
import FormField from '../../../../../../component/Formfield';
import RadioButtonGroup from '../../../../../../component/RadioButtonGroup';
import CheckboxField from '../../../../../../component/CheckboxField';
import { createLeaveType, updateLeaveType, getLeaveTypeById } from "../../../../../../services/leave";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { useLocation, useNavigate } from "react-router-dom";
import ErrorMsg from '../../../../../../component/ErrorMsg';
import DeleteWarning from '../../../../../../component/DeleteWarning';
import SaveDialog from '../../../../../../component/SaveDialog';
import { WizardCard, FormSection, FieldGrid, FieldLabel, WizardActions } from '../../../../../../component/wizard';
import { tokens } from '../../../../../../theme';

import { displayFont } from "../../../../../../theme";
const defaultLeave = {
    leaveCode: '', name: '', isPaid: true, isCountedTowardsHours: true,
    isPartiallyPaid: false, paymentPercentage: '', restrictToGender: false, genders: [],
    leaveEncashment: false, maximumEncashableDays: '', nonEncashableDays: '', earningComponent: '',
    isEarnedLeave: false, earnedLeaveFrequency: '', earnedLeaveRounding: '',
};

const LeaveTypeForm = () => {
    const [apiCall, setApiCall] = useState(false);
    const navigate = useNavigate();
    const notify = useNotify();
    const { state } = useLocation();
    const [mode, setMode] = useState('create');
    const [typeId, setTypeId] = useState(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
    const [apiError, setApiError] = useState("");
    const [leaveData, setLeaveData] = useState(defaultLeave);
    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        if (state && state.typeId) {
            setMode('view');
            setTypeId(state.typeId);
            fetchTypeData(state.typeId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    const fetchTypeData = async (id) => {
        try {
            const res = await getLeaveTypeById(id);
            const d = res.data || {};
            setLeaveData((p) => ({ ...p, ...d, genders: d.genders || [] }));
        } catch (error) {
            notify.error(error?.message || "Failed to load leave type");
        }
    };

    const viewing = mode === 'view';

    const handleSaveClick = async () => {
        const errs = {};
        if (!leaveData.leaveCode) errs.leaveCode = "Leave Code is required";
        if (!leaveData.name) errs.name = "Leave Name is required";
        if (leaveData.isPartiallyPaid && !leaveData.paymentPercentage) errs.paymentPercentage = "Payment Percentage is required";
        if (leaveData.leaveEncashment) {
            if (!leaveData.maximumEncashableDays) errs.maximumEncashableDays = "Maximum Encashable Days is required";
            if (!leaveData.nonEncashableDays) errs.nonEncashableDays = "Non Encashable Days is required";
            if (!leaveData.earningComponent) errs.earningComponent = "Earning Component is required";
        }
        if (leaveData.isEarnedLeave) {
            if (!leaveData.earnedLeaveFrequency) errs.earnedLeaveFrequency = "Earned Leave Frequency is required";
            if (!leaveData.earnedLeaveRounding) errs.earnedLeaveRounding = "Earned Leave Rounding is required";
        }
        if (Object.keys(errs).length) { setValidationErrors(errs); return; }

        const leaveForm = {
            leaveCode: leaveData.leaveCode, name: leaveData.name, isPaid: leaveData.isPaid,
            isCountedTowardsHours: leaveData.isCountedTowardsHours, isPartiallyPaid: leaveData.isPartiallyPaid,
            genders: leaveData.genders, isEarnedLeave: leaveData.isEarnedLeave,
        };
        if (leaveData.isPartiallyPaid) leaveForm.paymentPercentage = leaveData.paymentPercentage;
        if (leaveData.leaveEncashment) {
            leaveForm.maximumEncashableDays = leaveData.maximumEncashableDays;
            leaveForm.nonEncashableDays = leaveData.nonEncashableDays;
            leaveForm.earningComponent = leaveData.earningComponent;
        }
        if (leaveData.isEarnedLeave) {
            leaveForm.earnedLeaveFrequency = leaveData.earnedLeaveFrequency;
            leaveForm.earnedLeaveRounding = leaveData.earnedLeaveRounding;
        }

        try {
            setApiCall(true);
            if (mode === 'create') {
                const res = await createLeaveType(leaveForm);
                if (res.success) { notify.success("Leave type created"); handleNavigate(); }
                else { setApiError(res?.message || ""); notify.error(errorMessageFrom(res, "Could not create leave type")); }
            } else if (mode === 'edit') {
                const res = await updateLeaveType(typeId, leaveForm);
                if (res.success) setIsSaveDialogOpen(true);
                else { setApiError(res?.message || ""); notify.error(errorMessageFrom(res, "Could not update leave type")); }
            }
        } catch (error) {
            notify.error(error?.message || "Something went wrong saving the leave type");
        } finally {
            setApiCall(false);
        }
    };

    const handleNavigate = () => { setLeaveData(defaultLeave); navigate('/app/leave-type'); };
    const handleChange = (field, value) => {
        const updatedValue = field === "leaveCode" ? value?.toUpperCase() : value;
        setLeaveData((p) => ({ ...p, [field]: updatedValue }));
        setValidationErrors((p) => ({ ...p, [field]: "" }));
    };

    const title = mode === 'create' ? "Create Leave Type" : mode === 'edit' ? "Edit Leave Type" : leaveData.name || "Leave Type";

    return (
        <Box sx={{ width: "100%" }}>
            <TopBar MainHeading={"Leave Type"} breadcrumbs={[{ label: "Leave Type", href: "/app/leave-type" }, { label: title }]} />
            <WizardCard>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.5rem" }}>
                    <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600 }}>{title}</Box>
                    <BtnSecondary to="/app/leave-type" sx={{ borderRadius: "8px" }}>Back</BtnSecondary>
                </Stack>

                <FormSection title="Leave details">
                    <FieldGrid>
                        <Box>
                            <FieldLabel required>Leave Code</FieldLabel>
                            <FormField type="text" value={leaveData.leaveCode} validation={validationErrors.leaveCode}
                                onChange={(e) => handleChange('leaveCode', e.target.value)} disabled={viewing} />
                        </Box>
                        <Box>
                            <FieldLabel required>Leave Name</FieldLabel>
                            <FormField type="text" value={leaveData.name} validation={validationErrors.name}
                                onChange={(e) => handleChange('name', e.target.value)} disabled={viewing} />
                        </Box>
                    </FieldGrid>
                    <Box sx={{ mt: "0.5rem" }}>
                        <RadioButtonGroup label="Payroll selection" name="isPaid" value={leaveData.isPaid} disabled={viewing}
                            onChange={(e) => handleChange('isPaid', e.target.value)}
                            options={[{ value: true, label: 'Paid' }, { value: false, label: 'Unpaid' }]} />
                        <RadioButtonGroup label="Counted as hours worked?" name="isCountedTowardsHours" value={leaveData.isCountedTowardsHours} disabled={viewing}
                            onChange={(e) => handleChange('isCountedTowardsHours', e.target.value)}
                            options={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }]} />
                    </Box>
                </FormSection>

                <FormSection title="Pay & encashment">
                    <CheckboxField label="Is this a partially paid leave?" checked={leaveData.isPartiallyPaid} disabled={viewing}
                        onChange={(c) => handleChange('isPartiallyPaid', c)} labelStyle={{ fontSize: "0.9375rem", color: tokens.textMuted }} />
                    {leaveData.isPartiallyPaid && (
                        <Box sx={{ mt: "0.75rem" }}>
                            <FieldLabel required>Percent of daily salary per leave</FieldLabel>
                            <FormField type="number" value={leaveData.paymentPercentage} validation={validationErrors.paymentPercentage}
                                disabled={viewing} onChange={(e) => handleChange('paymentPercentage', e.target.value)} fullwidth />
                        </Box>
                    )}
                    <Box sx={{ mt: "0.75rem" }}>
                        <CheckboxField label="Leave encashment" checked={leaveData.leaveEncashment} disabled={viewing}
                            onChange={(c) => handleChange('leaveEncashment', c)} labelStyle={{ fontSize: "0.9375rem", color: tokens.textMuted }} />
                    </Box>
                    {leaveData.leaveEncashment && (
                        <FieldGrid columns={3}>
                            <Box><FieldLabel required>Max encashable</FieldLabel>
                                <FormField type="number" value={leaveData.maximumEncashableDays} validation={validationErrors.maximumEncashableDays}
                                    disabled={viewing} onChange={(e) => handleChange('maximumEncashableDays', e.target.value)} /></Box>
                            <Box><FieldLabel required>Non-encashable</FieldLabel>
                                <FormField type="number" value={leaveData.nonEncashableDays} validation={validationErrors.nonEncashableDays}
                                    disabled={viewing} onChange={(e) => handleChange('nonEncashableDays', e.target.value)} /></Box>
                            <Box><FieldLabel required>Earning component</FieldLabel>
                                <FormField type="text" value={leaveData.earningComponent} validation={validationErrors.earningComponent}
                                    disabled={viewing} onChange={(e) => handleChange('earningComponent', e.target.value)} /></Box>
                        </FieldGrid>
                    )}
                </FormSection>

                <FormSection title="Eligibility & accrual">
                    <CheckboxField label="Restrict to gender" checked={leaveData.restrictToGender} disabled={viewing}
                        onChange={(c) => handleChange('restrictToGender', c)} labelStyle={{ fontSize: "0.9375rem", color: tokens.textMuted }} />
                    {leaveData.restrictToGender && (
                        <RadioButtonGroup name="genders" value={leaveData.genders} disabled={viewing}
                            onChange={(e) => handleChange('genders', e.target.value)}
                            options={[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'others', label: 'Others' }]} />
                    )}
                    <Box sx={{ mt: "0.75rem" }}>
                        <CheckboxField label="Earned leave" checked={leaveData.isEarnedLeave} disabled={viewing}
                            onChange={(c) => handleChange('isEarnedLeave', c)} labelStyle={{ fontSize: "0.9375rem", color: tokens.textMuted }} />
                    </Box>
                    {leaveData.isEarnedLeave && (
                        <FieldGrid>
                            <Box><FieldLabel required>Frequency</FieldLabel>
                                <FormField type="select" value={leaveData.earnedLeaveFrequency} validation={validationErrors.earnedLeaveFrequency}
                                    options={['year', 'month']} disabled={viewing} onChange={(e) => handleChange('earnedLeaveFrequency', e.target.value)} /></Box>
                            <Box><FieldLabel required>Rounding</FieldLabel>
                                <FormField type="text" value={leaveData.earnedLeaveRounding} validation={validationErrors.earnedLeaveRounding}
                                    disabled={viewing} onChange={(e) => handleChange('earnedLeaveRounding', e.target.value)} /></Box>
                        </FieldGrid>
                    )}
                </FormSection>

                <ErrorMsg>{apiError}</ErrorMsg>
                <WizardActions>
                    {viewing ? (
                        <BtnPrimary onClick={() => setMode("edit")} sx={{ borderRadius: "8px" }}>Edit</BtnPrimary>
                    ) : (
                        <>
                            {mode === "edit" && (
                                <BtnSecondary onClick={() => setIsDeleteDialogOpen(true)} sx={{ borderRadius: "8px", color: tokens.danger, borderColor: tokens.dangerBorder, "&:hover": { borderColor: tokens.danger, background: tokens.dangerTint } }}>Delete</BtnSecondary>
                            )}
                            <BtnPrimary onClick={handleSaveClick} disabled={apiCall} sx={{ borderRadius: "8px" }}>
                                {apiCall ? <CircularProgress size={"1.25rem"} /> : "Save Leave Type"}
                            </BtnPrimary>
                        </>
                    )}
                </WizardActions>

                <SaveDialog open={isSaveDialogOpen} onClose={() => setIsSaveDialogOpen(false)} redirectLink={`/app/leave-type`} />
                <DeleteWarning open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)} redirectLink={'/app/leave-type'} />
            </WizardCard>
        </Box>
    );
};

export default LeaveTypeForm;
